import { useState } from "react";
import { Lock, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { decodeMessage } from "../lib/api";
import PostKit from "../components/PostKit";
import { T } from "@/locales/tr";

export default function Decode() {
  const [message, setMessage] = useState("");
  const [context, setContext] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!message.trim()) { toast.error(T("Colle un message à décoder.")); return; }
    setLoading(true); setResult("");
    try {
      const data = await decodeMessage({ message, context });
      setResult(data.text);
      toast.success(T("Décodage prêt."));
    } catch { toast.error(T("Le décodage a échoué.")); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="page-decode" className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Décodeur Papicoo")}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.98]">
        {T("Messages codés.")}<br />
        <span className="italic font-light">{T("Lecture coranique extraordinaire.")}</span>
      </h1>
      <p className="text-[color:var(--papicoo-muted)] mt-4 max-w-2xl leading-relaxed">
        {T("Argot, emojis cryptiques, sous-entendus, double-langage, slogans codés. Papicoo te livre")} <strong>{T("4 lectures")}</strong> {T(": littérale · symbolique · coranique extraordinaire · conseil chaleureux.")}
      </p>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="papicoo-card p-6 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Contexte (optionnel)")}</label>
            <input
              data-testid="decode-context"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder={T("Ex : reçu en DM Instagram d'un inconnu, ou commentaire TikTok…")}
              className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Message à décoder")}</label>
            <textarea
              data-testid="decode-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              placeholder='Ex : "🐍🔑🦅 — only the real ones see it 👁️"'
              className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
            />
          </div>
          <button data-testid="decode-go-btn" onClick={submit} disabled={loading} className="inline-flex items-center gap-2 px-5 py-3 rounded-full papicoo-btn-primary disabled:opacity-50">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
            {loading ? T("Décodage…") : T("Décoder le message")}
          </button>
        </div>
        <div data-testid="decode-result">
          {result ? (
            <>
            <div className="papicoo-card p-6 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <div className="font-display text-2xl text-[color:var(--papicoo-ink)]">{T("Quatre lectures")}</div>
                <button data-testid="decode-copy" onClick={() => { navigator.clipboard.writeText(result); toast.success(T("Copié.")); }} className="text-xs inline-flex items-center gap-1 text-[color:var(--papicoo-muted)] hover:text-[color:var(--papicoo-ink)]">
                  <Copy size={14} /> {T("Copier")}
                </button>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-[15px] leading-relaxed">{result}</pre>
            </div>
            <PostKit text={result} title={T("Publie le décodage avec la photo officielle")} />
            </>
          ) : (
            <div className="papicoo-card p-6 text-sm italic text-[color:var(--papicoo-muted)]">
              {T("Salaam — colle un message codé et Papicoo te l'éclaire en quatre lectures.")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
